import type { Task, Todo } from '@shh/shared';
import type { FamilyData } from './family';

/** A job as the app shows it: the data stays split (1.28: "kein Datenumzug vor dem Launch"). */
export type Job = { kind: 'task'; task: Task } | { kind: 'todo'; todo: Todo };

type Who = Pick<FamilyData, 'role' | 'meId'>;

/** A one-off job in the calendar is a virtual entry `todo_<id>` or `task_<id>` (1.28). */
export function jobOfEntry(f: Pick<FamilyData, 'todos' | 'tasks'>, entryId: string) {
  const [kind, id] = entryId.split(/_(.+)/);
  if (kind === 'todo') {
    const todo = f.todos.find((t) => t.id === id);
    return todo ? ({ kind, todo } satisfies Job) : undefined;
  }
  const task = f.tasks.find((t) => t.id === id);
  return kind === 'task' && task ? ({ kind, task } satisfies Job) : undefined;
}

const onlyMine = (a: Task['assignees'], me: string | undefined) =>
  a !== 'all' && a.length === 1 && a[0] === me;

/**
 * May the viewer change this job? Parents (and the unlocked wall) everything; teens only what is
 * theirs alone – a task as the rules say (`teenOwnTask`: made by them, for them, no stars); the
 * rules let teens change any to-do, but a teen should not change a sibling's. Kids never.
 */
export function canEditJob(f: Who, canParent: boolean, job: Job) {
  if (canParent) return true;
  if (f.role !== 'teen') return false;
  if (job.kind === 'task') {
    const t = job.task;
    return (
      t.createdBy === f.meId &&
      onlyMine(t.assignees, f.meId) &&
      t.points === 0 &&
      !t.requiresApproval
    );
  }
  return job.todo.createdBy === f.meId || onlyMine(job.todo.assignees, f.meId);
}
